import React from 'react';
import PropTypes from 'prop-types';
import PlacesAutocomplete from 'react-places-autocomplete';
import Box from '@mui/material/Box';

import AddressInput from 'components/AddressInput';
import Suggestions from 'bento-ordering/locations/components/Suggestions';
import { geocodePlaceId } from 'helpers/addressHelpers';
import Error from 'bento-ordering/base/components/Error';

import {
  ZERO_RESULTS,
  NOT_DELIVERABLE,
  NOT_DELIVERABLE_NO_PICKUP,
  EMPTY_STRING
} from 'constants/errorTypes';

// TODO: refactor to reference this from themes instead
import { WHITE } from 'styles/theme/colors';
import { GeocodeContext } from 'bento-ordering/context/GeocodeContext';

class AddressAutoCompleteInput extends React.Component {
  static propTypes = {
    handleSelect: PropTypes.func.isRequired,
    initialValue: PropTypes.string,
    label: PropTypes.string,
    includeAdornment: PropTypes.bool,
    isOnDarkBackground: PropTypes.bool,
    // local state in this component is initialized with error: ''
    error: PropTypes.oneOf([
      ZERO_RESULTS,
      NOT_DELIVERABLE,
      NOT_DELIVERABLE_NO_PICKUP,
      EMPTY_STRING
    ])
  };

  static defaultProps = {
    label: 'Your address',
    initialValue: '',
    includeAdornment: false,
    isOnDarkBackground: false,
    error: ''
  };

  static contextType = GeocodeContext;

  constructor(props) {
    super();

    this.state = {
      address: props.initialValue || '',
      error: ''
    };
  }

  handleAddressChange = address => {
    this.setState({ address });
  };

  /**
   * Filter the location list by distance once the user selects an address
   */
  handleAddressSelect = async (address, placeId) => {
    const { setIsMakingGeocodeRequest } = this.context;
    /**
     * we add this placeId check bc if the user types an address and hits enter without selecting
     * a google verified address, there is no placeId which throws an invalid request error from google
     */
    if (placeId) {
      this.setState({ address });

      setIsMakingGeocodeRequest(true);
      const { geocodedSuggestion, latLng } = await geocodePlaceId(placeId);

      this.props.handleSelect(
        geocodedSuggestion.address_components,
        latLng,
        placeId
      );
      // We set to false AFTER the call to handleSelect because we don't want the
      // loading spinner in LocationsContainer to stop and restart.
      setIsMakingGeocodeRequest(false);
    }
  };

  getErrorColor() {
    const { isOnDarkBackground } = this.props;

    if (isOnDarkBackground) {
      return WHITE;
    } else {
      return 'error';
    }
  }

  renderSuggestions(loading, suggestions, getSuggestionItemProps) {
    return (
      <Suggestions
        loading={loading}
        suggestions={suggestions}
        getSuggestionItemProps={getSuggestionItemProps}
      />
    );
  }

  renderAutoCompleteInput = ({
    getInputProps,
    getSuggestionItemProps,
    loading,
    suggestions
  }) => {
    const { label } = this.props;
    const { value, ...inputProps } = getInputProps();
    const error = this.state.error || this.props.error;
    const shouldDisplayError = !!error && !suggestions.length;
    const errorColor = this.getErrorColor();

    return (
      <div>
        <AddressInput
          id="address-input"
          {...inputProps}
          value={value}
          label={label}
          error={error}
          ariaAutocomplete="list"
          includeAdornment={this.props.includeAdornment}
          isOnDarkBackground={this.props.isOnDarkBackground}
        />

        {shouldDisplayError && (
          <Box mt={1}>
            <Error status={error} color={errorColor} />
          </Box>
        )}

        {this.renderSuggestions(loading, suggestions, getSuggestionItemProps)}
      </div>
    );
  };

  render() {
    return (
      <PlacesAutocomplete
        value={this.state.address}
        onChange={address => {
          this.handleAddressChange(address);
          this.setState({
            error: false
          });
        }}
        onSelect={this.handleAddressSelect}
        shouldFetchSuggestions={this.state.address.length > 3}
        onError={(status, clearSuggestions) => {
          this.setState({ error: status }, () => {
            clearSuggestions();
          });
        }}
      >
        {this.renderAutoCompleteInput}
      </PlacesAutocomplete>
    );
  }
}

export default AddressAutoCompleteInput;
